import { useEffect, useRef } from "react";
import type { PreConnectStep } from "../../../config/preConnectSteps";
import styles from "./PreConnectStepModal.module.css";

type Props = {
  open: boolean;
  // Integration title (e.g. "Monday") for the dialog's accessible name.
  title: string;
  step: PreConnectStep;
  onClose: () => void;
  // Resumes the interrupted connect dispatch. Fired from the button's click
  // handler, so the downstream window.open() still counts as a user gesture.
  onContinue: () => void;
};

// Explainer shown before the connect flow for integrations that need a manual
// step in the third-party app first (config/preConnectSteps.ts). Same native
// <dialog> + showModal() pattern as ApiKeyConnectModal for focus-trap /
// Escape / backdrop.
export const PreConnectStepModal = ({
  open,
  title,
  step,
  onClose,
  onContinue,
}: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-label={`Before you connect ${title}`}
      onCancel={(evt) => {
        evt.preventDefault();
        onClose();
      }}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <h2 className={styles.title}>{step.heading}</h2>
          <p className={styles.body}>{step.body}</p>
        </header>

        <a
          className={styles.linkBtn}
          href={step.linkHref}
          target="_blank"
          rel="noreferrer noopener"
        >
          {step.linkLabel}
          <span aria-hidden> →</span>
        </a>

        <p className={styles.note}>{step.note}</p>

        <footer className={styles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={styles.primaryBtn} onClick={onContinue}>
            {step.confirmLabel}
          </button>
        </footer>
      </div>
    </dialog>
  );
};
